import { Button, DatePicker, message, Popconfirm } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { get_meeting_list, removerace } from '../services/api.service';
import { isViewer } from '../services/auth.header';
import { formatdateYYYYMMDD, MLMeeting, MLRace } from '../types';
import './calendar.scss';
import { useLocalStorage } from '../localstorage';
import { DeleteOutlined, LeftCircleFilled, RightCircleFilled } from '@ant-design/icons';

/* -------------------------------------------------------------------------------- *\
|                               CalendarPage
\* -------------------------------------------------------------------------------- */
export const CalendarPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [lastselecteddate, setlastselecteddate] = useLocalStorage('lastselecteddate', formatdateYYYYMMDD(new Date()));
    const [loading, setLoading] = useState(true);
    const [currentdate, setCurrentDate] = useState<string>(lastselecteddate);
    const [meetings, setMeetings] = useState<MLMeeting[]>([]);
    const [daypicker, setDaypicker] = useState<Dayjs>(dayjs(lastselecteddate));
    const viewer = isViewer();

    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               refreshMeetings
    \* -------------------------------------------------------------------------------- */
    const refreshMeetings = async (currentdate: string) =>
    {
        get_meeting_list(currentdate).then((m: MLMeeting[]) =>
        {
            setMeetings(m);
        })
        .catch(error =>
        {
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               deleteMeeting
    \* -------------------------------------------------------------------------------- */
    const deleteMeeting = async (meeting: MLMeeting) =>
    {
        setLoading(true);
        try
        {
            await Promise.all(meeting.races.map((race: MLRace) => removerace(race)));
            message.success(`Réunion ${meeting.sic} supprimée`);
            await refreshMeetings(currentdate);
        }
        catch (error: any)
        {
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');
        }
        finally
        {
            setLoading(false);
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               changeCurrentDate
    \* -------------------------------------------------------------------------------- */
    const changeCurrentDate = (date: string) =>
    {
        setCurrentDate(date);
        setDaypicker(dayjs(date));
        setlastselecteddate(date);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               datepicker_handler
    \* -------------------------------------------------------------------------------- */
    function datepicker_handler(date: any)
    {
        if (!date) return;
        changeCurrentDate(date.format('YYYYMMDD'));
    }

    useEffect(() => {
        refreshMeetings(currentdate).then(() => setLoading(false));
        setLoading(true);
        // eslint-disable-next-line
    }, [currentdate])

    return (
        <LayoutSite>
            <div className="calendar">
                {loading ? <Loader /> : <></>}
                <div className='calendar__wrapper'>
                    <div className="calendar__header">
                        <div></div>
                    </div>
                    <div className='calendar__calendar'>
                        <div className='calendar__calendar__selector'>
                            <div className='calendar__label'>Choix du jour :</div>
                            <LeftCircleFilled className='calendar__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).subtract(1, 'day').format('YYYYMMDD'))} />
                            <DatePicker defaultValue={dayjs(currentdate)} value={daypicker} onChange={datepicker_handler} format={'DD/MM/YYYY'} />
                            <RightCircleFilled className='calendar__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).add(1, 'day').format('YYYYMMDD'))}/>
                        </div>
                        <Button type="primary" onClick={() => changeCurrentDate(formatdateYYYYMMDD(new Date()))}>Aujourd'hui</Button>
                    </div>
                </div>
                <div className='calendar__body'>
                {
                    meetings.map((m: MLMeeting) => 
                    {
                        return (
                            <div className='calendar__body__meeting' key={'meeting_' + m.sic}>
                                <div className='calendar__body__meeting__title' key={m.sic}>
                                    <span>{m.sic}</span>
                                    {
                                        !viewer &&
                                        <Popconfirm
                                            title="Supprimer la réunion"
                                            description={`Supprimer toutes les courses de ${m.sic} ?`}
                                            okText="Supprimer"
                                            cancelText="Annuler"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => deleteMeeting(m)}
                                        >
                                            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    }
                                </div>
                                {
                                    m.races.map((race: MLRace) => 
                                    {
                                        const meetingdate = new Date(race.meetingdate ? race.meetingdate : race.date);
                                        const date = new Date(race.date);
                                        
                                        return (
                                            <div className={'calendar__body__meeting__race'} key={race.number} onClick={() => { if (!isNaN(meetingdate.getTime())) navigate('/race/' + formatdateYYYYMMDD(meetingdate) + '/' + race.sic + '/' + race.number); }}>
                                                <div className='calendar__body__meeting__race__line'>
                                                    <div>C{race.number}</div>
                                                    <div>{!isNaN(date.getTime()) ? date.toLocaleTimeString().substring(0, 5) : ''}</div>
                                                    <div>- {race.distance}m</div>
                                                    <div> - {race.trackcode + " - " + race.type.toLowerCase() + " - count=" + race.competitors.length}</div>
                                                </div>
                                                <div>{race.name}</div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        )
                    })
                }
                </div>
            </div>
        </LayoutSite>
    );
}
